import { useState } from "react";

import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { usePostHog } from "posthog-js/react";

import { PersonalInformationDisclaimer } from "components/PersonalInformationDisclaimer";
import { ClientAppEvent } from "constants/index";
import {
  useDisclosure,
  useRoleContext,
  useShipmentPageContext,
  useWriterComponentState,
} from "hooks";
import { shipmentIsActive } from "utils";

interface Props {
  shipmentId: number | undefined;
  shipment: CondensedShipment;
}

export function SubmitQuestionMenu(props: Props) {
  const { shipmentId, shipment } = props;
  const { onRefresh } = useShipmentPageContext();
  const { role } = useRoleContext();
  const posthog = usePostHog();

  const { isOpen, onClose, onOpen } = useDisclosure();
  const [question, setQuestion] = useState<string>("");

  const { write, resetState, loading, error } = useWriterComponentState({
    writeConfig: {
      method: "post",
      url: `/shipments/${shipmentId}/questions`,
      body: { question },
    },
    errorMessage: "Failed to submit question",
    errorMessageKey: "error",
  });

  const postQuestion = () => {
    write(() => {
      posthog?.capture(ClientAppEvent.SHIPMENT_QUESTION_CREATED, {
        role,
        shipment_id: shipmentId,
        // Questions written outside the app are captured by the backend
        // listener under this same name, so both halves carry `source` and one
        // breakdown splits the series
        // (docs/engineering/design/qa_product_analytics_listener_design.md §8).
        source: "CUSTOMER_APP" satisfies SourceType,
      });
      if (onRefresh) {
        onRefresh();
      }
      onClose();
    });
  };

  const onModalClose = () => {
    resetState();
    onClose();
  };

  if (!shipmentId) {
    return null;
  }

  const postQuestionConfirmationDialog = () => (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Confirm Question</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Please confirm that you wish to send the following question:
        </DialogContentText>
        <Box mt="1rem" mb="1rem" textAlign="center">
          <em>{question}</em>
        </Box>
        <PersonalInformationDisclaimer />
        <Stack mt="1rem">
          <Typography color="red" align="center">
            {error}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onModalClose}>Cancel</Button>
        <Button onClick={postQuestion} autoFocus disabled={loading}>
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <div>
      {postQuestionConfirmationDialog()}
      <Stack direction="column">
        {shipmentIsActive(shipment) ? (
          <Stack direction="row" mt="1rem" justifyContent="center" spacing={2}>
            <TextField
              fullWidth
              label="Ask the shipper a question"
              onChange={(e) => setQuestion(e.target.value)}
            />
            <Button
              disabled={question.length < 1}
              variant="contained"
              sx={{ pl: "2rem", pr: "2rem" }}
              onClick={onOpen}
            >
              Send
            </Button>
          </Stack>
        ) : (
          <Typography align="center" variant="body2">
            Questions can only be created while the shipment is active.
          </Typography>
        )}
      </Stack>
    </div>
  );
}
